import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useCatalog } from '@/hooks/useCatalog';
import { toast } from 'sonner';
import type { Catalog, CatalogInsert, CatalogUpdate } from '@/types/db';
import type { Json } from '@/integrations/supabase/types';
import EligibilitySchemaEditor, { type EligibilitySchema } from './EligibilitySchemaEditor';
import OnboardingChecklistEditor, { type OnboardingChecklistItemConfig } from './OnboardingChecklistEditor';
interface CatalogItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  editItem?: Catalog | null;
}

export default function CatalogItemModal({ isOpen, onClose, editItem }: CatalogItemModalProps) {
  const { t } = useTranslation();
  const { createCatalogItem, updateCatalogItem } = useCatalog();
  const [isProcessing, setIsProcessing] = useState(false);
  const [eligibilitySchema, setEligibilitySchema] = useState<EligibilitySchema | null>(null);
  const [onboardingChecklist, setOnboardingChecklist] = useState<OnboardingChecklistItemConfig[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    type: 'service' as 'case' | 'service',
    description: '',
    base_price: '',
    dependent_price: '',
    allows_dependents: false,
    primary_person_label: 'Applicant',
    dependent_label: 'Dependent',
    has_secondary_person: false,
    secondary_person_label: '',
    max_dependents: '',
    pricing_type: 'flat' as 'flat' | 'per_person' | 'variable',
    estimated_duration_days: '',
    requirements: '',
    has_cost: false,
    cost_type: 'flat' as 'flat' | 'variable',
    cost_amount: '',
    cost_notes: '',
    is_active: true,
    // Role configuration
    enabled_roles: ['applicant'] as string[],
    role_labels: {
      applicant: 'Applicant',
      beneficiary: 'Beneficiary',
      petitioner: 'Petitioner',
    },
    petitioner_is_company: false,
  });

  useEffect(() => {
    if (editItem) {
      const catalogItem = editItem as any;
      setFormData({
        name: editItem.name || '',
        type: editItem.type || 'service',
        description: editItem.description || '',
        base_price: editItem.base_price?.toString() || '',
        dependent_price: editItem.dependent_price?.toString() || '',
        allows_dependents: editItem.allows_dependents || false,
        primary_person_label: editItem.primary_person_label || 'Applicant',
        dependent_label: editItem.dependent_label || 'Dependent',
        has_secondary_person: editItem.has_secondary_person || false,
        secondary_person_label: editItem.secondary_person_label || '',
        max_dependents: editItem.max_dependents?.toString() || '',
        pricing_type: editItem.pricing_type || 'flat',
        estimated_duration_days: editItem.estimated_duration_days?.toString() || '',
        requirements: editItem.requirements || '',
        has_cost: editItem.has_cost || false,
        cost_type: editItem.cost_type || 'flat',
        cost_amount: editItem.cost_amount?.toString() || '',
        cost_notes: editItem.cost_notes || '',
        is_active: editItem.is_active ?? true,
        // Role configuration
        enabled_roles: catalogItem.enabled_roles || ['applicant'],
        role_labels: catalogItem.role_labels || {
          applicant: 'Applicant',
          beneficiary: 'Beneficiary',
          petitioner: 'Petitioner',
        },
        petitioner_is_company: catalogItem.petitioner_is_company || false,
      });
      // Parse eligibility_schema and onboarding_checklist from the item
      setEligibilitySchema((editItem as any).eligibility_schema || null);
      setOnboardingChecklist((editItem as any).onboarding_checklist || []);
    } else {
      setFormData({
        name: '',
        type: 'service',
        description: '',
        base_price: '',
        dependent_price: '',
        allows_dependents: false,
        primary_person_label: 'Applicant',
        dependent_label: 'Dependent',
        has_secondary_person: false,
        secondary_person_label: '',
        max_dependents: '',
        pricing_type: 'flat',
        estimated_duration_days: '',
        requirements: '',
        has_cost: false,
        cost_type: 'flat',
        cost_amount: '',
        cost_notes: '',
        is_active: true,
        // Role configuration
        enabled_roles: ['applicant'],
        role_labels: {
          applicant: 'Applicant',
          beneficiary: 'Beneficiary',
          petitioner: 'Petitioner',
        },
        petitioner_is_company: false,
      });
      setEligibilitySchema(null);
      setOnboardingChecklist([]);
    }
  }, [editItem, isOpen]);

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error(t('catalog.nameIsRequired'));
      return;
    }

    if (!formData.base_price || parseFloat(formData.base_price) <= 0) {
      toast.error(t('catalog.basePriceRequired'));
      return;
    }

    setIsProcessing(true);
    try {
      const data: CatalogInsert = {
        name: formData.name.trim(),
        type: formData.type,
        description: formData.description.trim() || null,
        base_price: parseFloat(formData.base_price),
        dependent_price: formData.allows_dependents && formData.dependent_price 
          ? parseFloat(formData.dependent_price) 
          : null,
        allows_dependents: formData.allows_dependents,
        primary_person_label: formData.primary_person_label.trim() || 'Applicant',
        dependent_label: formData.dependent_label.trim() || 'Dependent',
        has_secondary_person: formData.has_secondary_person,
        secondary_person_label: formData.has_secondary_person && formData.secondary_person_label
          ? formData.secondary_person_label.trim()
          : null,
        max_dependents: formData.max_dependents ? parseInt(formData.max_dependents) : null,
        pricing_type: formData.pricing_type,
        estimated_duration_days: formData.estimated_duration_days 
          ? parseInt(formData.estimated_duration_days) 
          : null,
        requirements: formData.requirements.trim() || null,
        has_cost: formData.has_cost,
        cost_type: formData.has_cost ? formData.cost_type : undefined,
        cost_amount: formData.has_cost && formData.cost_amount 
          ? parseFloat(formData.cost_amount) 
          : null,
        cost_notes: formData.has_cost && formData.cost_notes 
          ? formData.cost_notes.trim() 
          : null,
        is_active: formData.is_active,
        eligibility_schema: (eligibilitySchema as unknown as Json) ?? null,
        onboarding_checklist: (formData.type === 'case' ? onboardingChecklist : []) as unknown as Json,
        // Role configuration
        enabled_roles: formData.type === 'case' ? formData.enabled_roles : ['applicant'],
        role_labels: (formData.type === 'case' ? formData.role_labels : null) as unknown as Json,
        petitioner_is_company: formData.type === 'case' ? formData.petitioner_is_company : false,
      };

      if (editItem) {
        await updateCatalogItem(editItem.id, data as CatalogUpdate);
        toast.success(t('catalog.updateSuccess'));
      } else {
        await createCatalogItem(data);
        toast.success(t('catalog.createSuccess'));
      }

      onClose();
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error(t('catalog.saveError'));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editItem ? t('catalog.editItem') : t('catalog.newCatalogItem')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">{t('catalog.basicInformation')}</h3>
            
            <div className="space-y-2">
              <Label htmlFor="name">{t('catalog.nameRequired')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('catalog.namePlaceholder')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">{t('catalog.typeRequired')}</Label>
              <Select
                value={formData.type}
                onValueChange={(value: 'case' | 'service') => 
                  setFormData({ ...formData, type: value })
                }
              >
                <SelectTrigger id="type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="case">{t('catalog.case')}</SelectItem>
                  <SelectItem value="service">{t('catalog.service')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t('catalog.descriptionLabel')}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('catalog.descriptionPlaceholder')}
                rows={3}
              />
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">{t('catalog.pricing')}</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="base_price">{t('catalog.basePriceUsd')}</Label>
                <Input
                  id="base_price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.base_price}
                  onChange={(e) => setFormData({ ...formData, base_price: e.target.value })}
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="pricing_type">{t('catalog.pricingType')}</Label>
                <Select
                  value={formData.pricing_type}
                  onValueChange={(value: 'flat' | 'per_person' | 'variable') => 
                    setFormData({ ...formData, pricing_type: value })
                  }
                >
                  <SelectTrigger id="pricing_type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flat">{t('catalog.flat')}</SelectItem>
                    <SelectItem value="per_person">{t('catalog.perPerson')}</SelectItem>
                    <SelectItem value="variable">{t('catalog.variable')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="allows_dependents"
                checked={formData.allows_dependents}
                onCheckedChange={(checked) => 
                  setFormData({ ...formData, allows_dependents: checked })
                }
              />
              <Label htmlFor="allows_dependents">{t('catalog.allowsDependents')}</Label>
            </div>

            {formData.allows_dependents && (
              <div className="grid grid-cols-2 gap-4 pl-6">
                <div className="space-y-2">
                  <Label htmlFor="dependent_price">{t('catalog.pricePerDependent')}</Label>
                  <Input
                    id="dependent_price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.dependent_price}
                    onChange={(e) => setFormData({ ...formData, dependent_price: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max_dependents">{t('catalog.maxDependents')}</Label>
                  <Input
                    id="max_dependents"
                    type="number"
                    min="0"
                    value={formData.max_dependents}
                    onChange={(e) => setFormData({ ...formData, max_dependents: e.target.value })}
                    placeholder={t('catalog.noLimit')}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Cost Information */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Switch
                id="has_cost"
                checked={formData.has_cost}
                onCheckedChange={(checked) => 
                  setFormData({ ...formData, has_cost: checked })
                }
              />
              <Label htmlFor="has_cost">{t('catalog.hasCost')}</Label>
            </div>

            {formData.has_cost && (
              <div className="space-y-4 pl-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cost_type">{t('catalog.costType')}</Label>
                    <Select
                      value={formData.cost_type}
                      onValueChange={(value: 'flat' | 'variable') => 
                        setFormData({ ...formData, cost_type: value })
                      }
                    >
                      <SelectTrigger id="cost_type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="flat">{t('catalog.flat')}</SelectItem>
                        <SelectItem value="variable">{t('catalog.variable')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cost_amount">
                      {t('catalog.costAmount')} {formData.cost_type === 'variable' ? '(%)' : '(USD)'}
                    </Label>
                    <Input
                      id="cost_amount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.cost_amount}
                      onChange={(e) => setFormData({ ...formData, cost_amount: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cost_notes">{t('catalog.costNotes')}</Label>
                  <Textarea
                    id="cost_notes"
                    value={formData.cost_notes}
                    onChange={(e) => setFormData({ ...formData, cost_notes: e.target.value })}
                    placeholder={t('catalog.costNotesPlaceholder')}
                    rows={2}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Additional Details */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">{t('catalog.additionalDetails')}</h3>
            
            <div className="space-y-2">
              <Label htmlFor="estimated_duration_days">{t('catalog.estimatedDuration')}</Label>
              <Input
                id="estimated_duration_days"
                type="number"
                min="0"
                value={formData.estimated_duration_days}
                onChange={(e) => setFormData({ ...formData, estimated_duration_days: e.target.value })}
                placeholder="e.g., 90"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="requirements">{t('catalog.requirements')}</Label>
              <Textarea
                id="requirements"
                value={formData.requirements}
                onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                placeholder={t('catalog.requirementsPlaceholder')}
                rows={3}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => 
                  setFormData({ ...formData, is_active: checked })
                }
              />
              <Label htmlFor="is_active">{t('catalog.activeItem')}</Label>
            </div>
          </div>

          {/* Role Configuration - Only for Case type */}
          {formData.type === 'case' && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">{t('catalog.roleConfiguration', 'Configuração de Roles')}</h3>
              <p className="text-xs text-muted-foreground">
                {t('catalog.roleConfigurationDescription', 'Selecione quais roles estão disponíveis para este tipo de caso')}
              </p>

              {/* Applicant */}
              <div className="space-y-2 p-3 border rounded-lg">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="role_applicant"
                    checked={formData.enabled_roles.includes('applicant')}
                    onCheckedChange={(checked) => {
                      const newRoles = checked 
                        ? [...formData.enabled_roles, 'applicant']
                        : formData.enabled_roles.filter(r => r !== 'applicant');
                      setFormData({ ...formData, enabled_roles: newRoles });
                    }}
                  />
                  <Label htmlFor="role_applicant" className="font-medium">Applicant</Label>
                </div>
                {formData.enabled_roles.includes('applicant') && (
                  <div className="pl-6 space-y-2">
                    <Label className="text-xs text-muted-foreground">Label customizado</Label>
                    <Input
                      value={formData.role_labels.applicant || 'Applicant'}
                      onChange={(e) => setFormData({
                        ...formData,
                        role_labels: { ...formData.role_labels, applicant: e.target.value }
                      })}
                      placeholder="Applicant"
                    />
                  </div>
                )}
              </div>

              {/* Beneficiary */}
              <div className="space-y-2 p-3 border rounded-lg">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="role_beneficiary"
                    checked={formData.enabled_roles.includes('beneficiary')}
                    onCheckedChange={(checked) => {
                      const newRoles = checked 
                        ? [...formData.enabled_roles, 'beneficiary']
                        : formData.enabled_roles.filter(r => r !== 'beneficiary');
                      setFormData({ ...formData, enabled_roles: newRoles });
                    }}
                  />
                  <Label htmlFor="role_beneficiary" className="font-medium">Beneficiary</Label>
                </div>
                {formData.enabled_roles.includes('beneficiary') && (
                  <div className="pl-6 space-y-2">
                    <Label className="text-xs text-muted-foreground">Label customizado</Label>
                    <Input
                      value={formData.role_labels.beneficiary || 'Beneficiary'}
                      onChange={(e) => setFormData({
                        ...formData,
                        role_labels: { ...formData.role_labels, beneficiary: e.target.value }
                      })}
                      placeholder="Beneficiary"
                    />
                  </div>
                )}
              </div>

              {/* Petitioner */}
              <div className="space-y-2 p-3 border rounded-lg">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="role_petitioner"
                    checked={formData.enabled_roles.includes('petitioner')}
                    onCheckedChange={(checked) => {
                      const newRoles = checked 
                        ? [...formData.enabled_roles, 'petitioner']
                        : formData.enabled_roles.filter(r => r !== 'petitioner');
                      setFormData({ ...formData, enabled_roles: newRoles });
                    }}
                  />
                  <Label htmlFor="role_petitioner" className="font-medium">Petitioner</Label>
                </div>
                {formData.enabled_roles.includes('petitioner') && (
                  <div className="pl-6 space-y-3">
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Label customizado</Label>
                      <Input
                        value={formData.role_labels.petitioner || 'Petitioner'}
                        onChange={(e) => setFormData({
                          ...formData,
                          role_labels: { ...formData.role_labels, petitioner: e.target.value }
                        })}
                        placeholder="Petitioner"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="petitioner_is_company"
                        checked={formData.petitioner_is_company}
                        onCheckedChange={(checked) => setFormData({ ...formData, petitioner_is_company: checked })}
                      />
                      <Label htmlFor="petitioner_is_company" className="text-sm">
                        {t('catalog.petitionerIsCompany', 'É uma empresa (não pessoa física)')}
                      </Label>
                    </div>
                  </div>
                )}
              </div>

              {/* Validation message */}
              {formData.enabled_roles.filter(r => r !== 'dependent').length === 0 && (
                <p className="text-xs text-destructive">
                  Selecione pelo menos uma role (Applicant, Beneficiary ou Petitioner)
                </p>
              )}
            </div>
          )}

          {/* Onboarding Checklist */}
          {formData.type === 'case' && (
            <OnboardingChecklistEditor
              value={onboardingChecklist}
              onChange={setOnboardingChecklist}
            />
          )}

          {/* Eligibility Schema */}
          {formData.type === 'case' && (
            <EligibilitySchemaEditor
              value={eligibilitySchema}
              onChange={setEligibilitySchema}
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isProcessing}>
            {t('catalog.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isProcessing}>
            {isProcessing ? t('catalog.saving') : editItem ? t('catalog.update') : t('catalog.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
